import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Calculator, FileText, BookOpen, ExternalLink } from 'lucide-react';
import { resources } from '../mock';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const iconMap = {
  calculator: Calculator,
  pdf: FileText,
  article: BookOpen
};

const ResourcesPage = () => {
  const [activeTab, setActiveTab] = useState('all');

  const filteredResources = activeTab === 'all' 
    ? resources 
    : resources.filter(r => r.type === activeTab);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[350px] flex items-center justify-center overflow-hidden bg-[#0077B6]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0077B6] to-[#005A8C]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3 text-white">Resources</h1>
          <p className="text-lg text-blue-100">
            Tools and guides to help you make informed financial decisions
          </p>
        </div>
      </section>

      {/* Resources Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-8 flex justify-center">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="inline-flex bg-[#F0F7FF] border border-blue-100 p-1 rounded-lg">
                <TabsTrigger value="all" className="px-6 py-2 data-[state=active]:bg-[#0077B6] data-[state=active]:text-white text-gray-700 rounded-md font-medium">All</TabsTrigger>
                <TabsTrigger value="calculator" className="px-6 py-2 data-[state=active]:bg-[#0077B6] data-[state=active]:text-white text-gray-700 rounded-md font-medium">Calculators</TabsTrigger>
                <TabsTrigger value="article" className="px-6 py-2 data-[state=active]:bg-[#0077B6] data-[state=active]:text-white text-gray-700 rounded-md font-medium">Guides</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredResources.map((resource) => {
              const Icon = iconMap[resource.type] || BookOpen;
              return (
                <Card key={resource.id} className="bg-[#F0F7FF] border border-blue-100 hover:border-[#0077B6] transition-all hover:shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-12 h-12 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center">
                        <Icon className="h-6 w-6 text-[#0077B6]" />
                      </div>
                      <span className="text-xs font-bold text-white bg-[#D4AF37] px-2 py-1 rounded">
                        {resource.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">{resource.title}</h3>
                    <p className="text-gray-600 text-base mb-4">{resource.description}</p>
                    <Button 
                      asChild
                      variant="outline" 
                      size="sm" 
                      className="w-full border-2 border-[#0077B6] text-[#0077B6] hover:bg-[#0077B6] hover:text-white font-bold"
                    >
                      <a href={resource.link} target="_blank" rel="noopener noreferrer">
                        {resource.type === 'calculator' ? 'Open Calculator' : 'Read Guide'}
                        <ExternalLink className="ml-2 h-3 w-3" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Info Sections */}
      <section className="py-12 bg-[#F0F7FF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="text-center bg-white border border-gray-200 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mx-auto mb-3">
                  <Calculator className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="font-bold text-gray-800 text-lg mb-2">Financial Calculators</h3>
                <p className="text-gray-600">Interactive tools to help you plan and budget</p>
              </CardContent>
            </Card>
            <Card className="text-center bg-white border border-gray-200 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#D4AF37]/10 border-2 border-[#D4AF37] flex items-center justify-center mx-auto mb-3">
                  <FileText className="h-7 w-7 text-[#D4AF37]" />
                </div>
                <h3 className="font-bold text-gray-800 text-lg mb-2">Official Guides</h3>
                <p className="text-gray-600">Government and industry resources</p>
              </CardContent>
            </Card>
            <Card className="text-center bg-white border border-gray-200 hover:shadow-lg transition-all">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-xl bg-[#0077B6]/10 border-2 border-[#0077B6] flex items-center justify-center mx-auto mb-3">
                  <BookOpen className="h-7 w-7 text-[#0077B6]" />
                </div>
                <h3 className="font-bold text-gray-800 text-lg mb-2">Educational Articles</h3>
                <p className="text-gray-600">Learn about financial topics that matter</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">Need Personalized Advice?</h2>
          <p className="text-blue-100 text-lg mb-6">
            Our resources are a great starting point, but nothing beats personalized guidance from our expert advisors.
          </p>
          <Button size="lg" className="bg-[#D4AF37] hover:bg-[#B8962E] text-[#003D5C] font-bold text-lg px-8">
            <a href="tel:18772399060">1-877-239-9060</a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;
